import { useEffect, useMemo, useState } from 'react'
import { formatOfficerDate, formatNumberString } from '../../lib/officerFormat'
import DocumentLink from '../documents/DocumentLink'
import SourceDocumentList from '../documents/SourceDocumentList'
import type { ReaderContext } from '../../lib/documentReader'
import {
  formatBookValue,
  pairedCapitalRows,
  pairedCapitalDisplayValue,
  toBookReviewView,
  visibleBookFields,
  bookFieldEditor,
  parseBookEditorValue,
  type BookEditorModel,
  type BookEditorSchema,
  type BookFieldView,
  type BookObjectView,
  type BookReview,
  type BookReviewAction,
  type BookSourceLink,
} from './bookReview'
import './book-review.css'

export interface BookRecordComparisonProps {
  review: BookReview | null
  onAction?: (action: BookReviewAction, object?: BookObjectView) => void
  onFieldChange?: (object: BookObjectView, field: BookFieldView, value: unknown) => void
  pendingActionId?: string | null
  className?: string
  embedded?: boolean
}

function sourceLabel(source: BookSourceLink): string {
  const title = source.document.display_title ?? 'Document'
  return source.page ? `${title} · p.${source.page}` : title
}

function sourceContext(source: BookSourceLink): ReaderContext {
  return {
    annotationId: source.annotationId,
    page: source.page,
    returnTo:
      typeof window === 'undefined' ? undefined : window.location.pathname + window.location.search,
  }
}

const reconsiderLabels = {
  new_source: 'a new source arrives',
  changed_action: 'this event changes',
  admission: 'admission',
  funding: 'funding',
  distribution: 'a distribution',
  default: 'a default',
  transfer: 'a transfer',
  expiry: 'expiry',
} as const

const reportingQualificationLabels = {
  unaudited_or_subject_to_adjustment: 'Unaudited or subject to adjustment',
  future_rights_not_established: 'Future rights not established',
  valuation_basis_note: 'Valuation basis',
  generic_scope_label: 'Generic instrument label',
} as const

function SourceLinks({ sources, testId }: { sources: readonly BookSourceLink[]; testId: string }) {
  if (!sources.length) return null
  return (
    <span className="book-record-comparison__sources" data-testid={testId}>
      {sources.map((source, index) => (
        <DocumentLink
          key={`${source.document.document_id}:${source.page ?? 'document'}:${index}`}
          document={source.document}
          context={sourceContext(source)}
          className="book-record-comparison__source-link"
        >
          {sourceLabel(source)}
        </DocumentLink>
      ))}
    </span>
  )
}

function CurrentValue({ field, objectId }: { field: BookFieldView; objectId: string }) {
  return (
    <div className="book-record-comparison__value book-record-comparison__value--current">
      {field.currentSources[0] ? (
        <DocumentLink
          document={field.currentSources[0].document}
          context={sourceContext(field.currentSources[0])}
          title={sourceLabel(field.currentSources[0])}
        >
          {field.currentText}
        </DocumentLink>
      ) : (
        <span>{field.currentText}</span>
      )}
      <SourceLinks
        sources={field.currentSources.slice(1)}
        testId={`book-current-sources-${objectId}-${field.key}`}
      />
    </div>
  )
}

function DocumentValue({
  field,
  objectId,
  object,
}: {
  field: BookFieldView
  objectId: string
  object: BookObjectView
}) {
  const leg = object.capitalLeg
  if (leg && field.key === 'units_held')
    return (
      <div className="book-record-comparison__value" data-testid="book-effect-calculation">
        {formatNumberString(leg.current_value)} + ({formatNumberString(leg.effect_delta)}) ={' '}
        {formatNumberString(leg.resulting_value)} {leg.currency}
      </div>
    )
  if (leg && field.key === 'balance_as_of')
    return (
      <div className="book-record-comparison__value">
        Balance after this movement · {formatOfficerDate(leg.resulting_date)}
      </div>
    )
  return (
    <div className="book-record-comparison__value book-record-comparison__value--document">
      {field.documentClaims.length ? (
        field.documentClaims.map((claim) => (
          <DocumentLink
            key={claim.key}
            document={claim.source.document}
            context={sourceContext(claim.source)}
            title={sourceLabel(claim.source)}
            data-testid={`book-document-claim-${objectId}-${field.key}`}
          >
            {claim.text}
            {claim.dateText ? <> · {claim.dateText}</> : null}
          </DocumentLink>
        ))
      ) : (
        <span>{field.documentText}</span>
      )}
    </div>
  )
}

function concreteSchema(schema: BookEditorSchema): BookEditorSchema {
  const alternatives = schema.anyOf ?? schema.oneOf
  if (!alternatives?.length) return schema
  return alternatives.find((item) => item.type !== 'null') ?? alternatives[0]
}

function inputValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

function StructuredEditor({
  schema,
  value,
  label,
  path,
  onChange,
  depth = 0,
}: {
  schema: BookEditorSchema
  value: unknown
  label: string
  path: string
  onChange: (value: unknown) => void
  depth?: number
}) {
  const resolved = concreteSchema(schema)
  if (depth >= 3) return <span>{formatBookValue({ state: 'value', value })}</span>
  const options = resolved.enum ?? []
  if (options.length) {
    return (
      <select
        aria-label={`${label} proposed`}
        data-proposed-field={path}
        value={inputValue(value)}
        onChange={(event) => onChange(parseBookEditorValue(resolved, event.target.value))}
      >
        <option value="">Choose…</option>
        {options.map((option) => (
          <option
            key={String(option)
              .replace(/_/g, ' ')
              .replace(/^./, (char) => char.toUpperCase())}
            value={String(option)}
          >
            {String(option)
              .replace(/_/g, ' ')
              .replace(/^./, (char) => char.toUpperCase())}
          </option>
        ))}
      </select>
    )
  }
  if (resolved.type === 'boolean') {
    return (
      <select
        aria-label={`${label} proposed`}
        data-proposed-field={path}
        value={inputValue(value)}
        onChange={(event) => onChange(parseBookEditorValue(resolved, event.target.value))}
      >
        <option value="">Not stated</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    )
  }
  if (resolved.type === 'object' || resolved.properties) {
    const object =
      value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {}
    const properties = Object.entries(resolved.properties ?? {})
    if (!properties.length) return <span>{inputValue(value) || 'Not stated'}</span>
    return (
      <div className="book-record-comparison__structured" data-structured-path={path}>
        {properties.slice(0, 30).map(([key, property]) => (
          <label key={key} className="book-record-comparison__structured-field">
            <span>{key.replace(/_/g, ' ')}</span>
            <StructuredEditor
              schema={property}
              value={object[key]}
              label={key}
              path={`${path}.${key}`}
              depth={depth + 1}
              onChange={(next) => onChange({ ...object, [key]: next })}
            />
          </label>
        ))}
      </div>
    )
  }
  if (resolved.type === 'array' || resolved.items) {
    const items = Array.isArray(value) ? value : []
    if (!items.length) return <span>Not stated</span>
    return (
      <div className="book-record-comparison__structured-array" data-structured-path={path}>
        {items.slice(0, 30).map((item, index) => (
          <StructuredEditor
            key={`${path}.${index}`}
            schema={resolved.items ?? { type: 'string' }}
            value={item}
            label={`${label} ${index + 1}`}
            path={`${path}.${index}`}
            depth={depth + 1}
            onChange={(next) => {
              const nextItems = [...items]
              nextItems[index] = next
              onChange(nextItems)
            }}
          />
        ))}
      </div>
    )
  }
  const type = resolved.format === 'date' ? 'date' : 'text'
  return (
    <input
      aria-label={`${label} proposed`}
      data-proposed-field={path}
      type={type}
      inputMode={resolved.type === 'number' || resolved.type === 'integer' ? 'decimal' : undefined}
      value={inputValue(value)}
      onChange={(event) => onChange(parseBookEditorValue(resolved, event.target.value))}
    />
  )
}

function ProposedValue({
  object,
  field,
  draft,
  hasDraft,
  onChange,
}: {
  object: BookObjectView
  field: BookFieldView
  draft: unknown
  hasDraft: boolean
  onChange: (value: unknown) => void
}) {
  if (!field.editable) {
    return (
      <div className="book-record-comparison__value book-record-comparison__value--proposed">
        <span>{field.proposedText}</span>
      </div>
    )
  }
  const editor: BookEditorModel = bookFieldEditor(field)
  const value = hasDraft
    ? draft
    : field.proposed.state === 'value'
      ? field.proposed.value
      : undefined
  return (
    <div className="book-record-comparison__value book-record-comparison__value--proposed">
      <StructuredEditor
        schema={editor.schema}
        value={value}
        label={field.label}
        path={`${object.proposal_id}.${field.key}`}
        onChange={onChange}
      />
      {field.validationIssue ? (
        <small className="book-record-comparison__validation">{field.validationIssue}</small>
      ) : null}
    </div>
  )
}

function ActionButtons({
  actions,
  object,
  pendingActionId,
  onAction,
}: {
  actions: readonly BookReviewAction[]
  object?: BookObjectView
  pendingActionId?: string | null
  onAction?: (action: BookReviewAction, object?: BookObjectView) => void
}) {
  if (!actions.length) return null
  return (
    <div className="book-record-comparison__actions" data-testid="book-object-actions">
      {actions.map((action) => {
        const pending = action.pending || pendingActionId === action.id
        return (
          <button
            key={action.id}
            type="button"
            className={
              action.kind === 'book_all'
                ? 'book-record-comparison__action book-record-comparison__action--primary'
                : 'book-record-comparison__action'
            }
            disabled={Boolean(action.disabled || pending)}
            title={action.disabled_reason ?? undefined}
            data-action-id={action.id}
            onClick={() => onAction?.(action, object)}
          >
            {pending ? 'Pending…' : action.label}
          </button>
        )
      })}
    </div>
  )
}

function ObjectComparison({
  object,
  pendingActionId,
  onAction,
  onFieldChange,
}: {
  object: BookObjectView
  pendingActionId?: string | null
  onAction?: (action: BookReviewAction, object?: BookObjectView) => void
  onFieldChange?: (object: BookObjectView, field: BookFieldView, value: unknown) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, unknown>>({})
  const fields = visibleBookFields(object.fields, expanded)
  const deferredIssues =
    object.initial_capital_review?.deferred_issues ??
    object.capital_final_review?.deferred_issues ??
    object.capital_calculation?.deferred_issues ??
    []
  const noncoreFlags =
    object.initial_capital_review?.noncore_flags ??
    object.capital_final_review?.noncore_flags ??
    object.capital_calculation?.noncore_flags ??
    object.standing_review?.noncore_flags ??
    []
  const title = object.title ?? object.target_type[0].toUpperCase() + object.target_type.slice(1)

  useEffect(() => {
    setExpanded(false)
    setDrafts({})
  }, [object.target_id])

  return (
    <section
      className={`book-record-comparison__object${object.isNew ? ' is-new' : ''}`}
      data-object-role={object.role ?? 'primary'}
      data-object-kind={object.target_type}
      data-object-id={object.target_id}
      data-proposal-id={object.proposal_id}
      data-testid={`book-object--`}
    >
      <header className="book-record-comparison__object-header">
        <div>
          <h3>{title}</h3>
          <p>
            {object.role === 'effect'
              ? 'Result of this trade'
              : object.proposal_status === 'confirmed'
                ? 'Approved · awaiting Record confirmation'
                : object.isNew
                  ? 'New'
                  : 'Update'}
          </p>
          {object.nominal_citation_review ? (
            <div role="note" data-testid="book-nominal-citation-review">
              <p>
                Nominal amount needs source review. The reader returned{' '}
                {object.nominal_citation_review.amount === null
                  ? 'no amount'
                  : formatNumberString(object.nominal_citation_review.amount)}
                . Its quotation could not be verified on the cited page.
              </p>
              <p>
                {object.nominal_citation_review.cited_page === null
                  ? 'Original page unavailable.'
                  : `Reader cited page ${object.nominal_citation_review.cited_page} (unverified).`}
              </p>
              {object.nominal_citation_review.quote ? (
                <blockquote>{object.nominal_citation_review.quote}</blockquote>
              ) : (
                <p>No usable quotation was retained.</p>
              )}
              {object.proposal_status === 'pending' ? (
                <p>
                  An explicit review decision is required. Booking as stated is limited to unchanged
                  trades with no other unverified source details.
                </p>
              ) : (
                <p>The original reader citation remains unverified after the review decision.</p>
              )}
              <DocumentLink document={object.nominal_citation_review.document}>
                Review the original document
              </DocumentLink>
            </div>
          ) : null}
          {object.draft_date_review ? (
            <p role="note">
              Provisional draft
              {object.draft_date_review.date_origin === 'document_receipt'
                ? ' · date taken from the document receipt: '
                : object.draft_date_review.date_origin === 'source_proposed_date'
                  ? ' · proposed date: '
                  : ' · date needs review'}
              {object.draft_date_review.effective_date
                ? formatOfficerDate(object.draft_date_review.effective_date)
                : null}
            </p>
          ) : null}
          {object.opening_position_review ? (
            <p role="note">
              Opening{' '}
              {object.opening_position_review.position_role === 'commitment'
                ? 'commitment'
                : 'undrawn'}{' '}
              balance
              {' · '}
              {formatOfficerDate(object.opening_position_review.financial_date)}.
              {object.opening_position_review.derived ? (
                <>
                  {' '}
                  Derived from the signed commitment of{' '}
                  {formatNumberString(object.opening_position_review.signed_commitment)}{' '}
                  {object.opening_position_review.currency}.
                </>
              ) : null}
            </p>
          ) : null}
          {object.standing_review ? (
            <div>
              <p role="note">
                {object.standing_review.position_role === 'commitment'
                  ? 'Commitment balance'
                  : 'Undrawn balance'}{' '}
                · {formatOfficerDate(object.standing_review.financial_date)}.
                {object.standing_review.missing_requirements.includes('governing_terms')
                  ? ' Governing terms remain missing.'
                  : null}
              </p>
              {object.standing_review.reporting_qualifications?.map((note, index) => (
                <p role="note" key={index} data-testid="standing-reporting-qualification">
                  <strong>{reportingQualificationLabels[note.kind]}</strong>. Review found no effect
                  on the current reported facts. {note.reason}{' '}
                  <SourceLinks
                    testId="standing-qualification-sources"
                    sources={note.documents.map((document) => ({
                      document,
                      page: document.page,
                      quote: document.snippet,
                    }))}
                  />
                </p>
              ))}
            </div>
          ) : null}
        </div>
        <ActionButtons
          actions={object.actions}
          object={object}
          pendingActionId={pendingActionId}
          onAction={onAction}
        />
      </header>
      <div className="book-record-comparison__columns" role="table" aria-label={`${title} fields`}>
        <div className="book-record-comparison__head" role="row">
          <span role="columnheader">Field</span>
          <span role="columnheader">Current Record</span>
          <span role="columnheader">
            {object.role === 'effect' ? 'Calculation' : 'Document states'}
          </span>
          <span role="columnheader">Proposed Record</span>
        </div>
        {fields.length ? (
          fields.map((field) => {
            const draftKey = `${object.target_id}:${field.key}`
            return (
              <div
                key={field.key}
                className={`book-record-comparison__row${field.changed ? ' is-changed' : ''}${field.mandatoryEmpty ? ' is-required-empty' : ''}`}
                data-field-key={field.key}
                data-changed={field.changed}
                role="row"
              >
                <div className="book-record-comparison__field-label" role="cell">
                  <span>{field.label}</span>
                  {field.required ? <small>Required</small> : null}
                </div>
                <div role="cell">
                  <CurrentValue field={field} objectId={object.target_id} />
                </div>
                <div role="cell">
                  <DocumentValue field={field} objectId={object.target_id} object={object} />
                </div>
                <div role="cell">
                  <ProposedValue
                    object={object}
                    field={field}
                    draft={drafts[draftKey]}
                    hasDraft={Object.prototype.hasOwnProperty.call(drafts, draftKey)}
                    onChange={(value) => {
                      setDrafts((previous) => ({ ...previous, [draftKey]: value }))
                      onFieldChange?.(object, field, value)
                    }}
                  />
                </div>
              </div>
            )
          })
        ) : (
          <p className="book-record-comparison__empty">No fields are available for this object.</p>
        )}
      </div>
      {object.provisional_capital_review ? (
        <section
          className="book-record-comparison__capital-calculation"
          aria-label="Provisional commitment review"
        >
          <h4>Provisional commitment</h4>
          <p>
            {object.provisional_capital_review.date_origin === 'document_receipt'
              ? 'Provisional date from the original document receipt'
              : 'Proposed date stated in the draft'}
            {' · '}
            {formatOfficerDate(object.provisional_capital_review.financial_date)}
          </p>
          <table aria-label="Proposed commitment effects">
            <thead>
              <tr>
                <th>Balance</th>
                <th>Current Record</th>
                <th>Proposed amount</th>
              </tr>
            </thead>
            <tbody>
              {pairedCapitalRows(object.provisional_capital_review.legs, [
                'current_value',
                'effect_delta',
              ] as const).map((row) => (
                <tr key={row.role}>
                  <th>{row.label}</th>
                  <td>{formatCapitalValue(row.role, row.values.current_value)}</td>
                  <td>
                    {formatCapitalValue(row.role, row.values.effect_delta)}{' '}
                    {object.provisional_capital_review!.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            The draft remains provisional. It does not establish execution, payment or evidence of a
            current balance.
          </p>
        </section>
      ) : null}
      {object.capital_final_review ? (
        <section
          className="book-record-comparison__capital-calculation"
          aria-label="Signed commitment review"
        >
          <h4>Signed commitment replaces the provisional one</h4>
          <table aria-label="Provisional and signed terms">
            <thead>
              <tr>
                <th>Term</th>
                <th>Provisional (draft)</th>
                <th>Signed agreement</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Date</th>
                <td>
                  {formatOfficerDate(object.capital_final_review.provisional_date)}
                  {object.capital_final_review.provisional_date_origin === 'document_receipt'
                    ? ' (document receipt)'
                    : ' (proposed in the draft)'}
                </td>
                <td>{formatOfficerDate(object.capital_final_review.final_date)}</td>
              </tr>
              <tr>
                <th>Commitment</th>
                <td>
                  {formatNumberString(object.capital_final_review.provisional_amount)}{' '}
                  {object.capital_final_review.currency}
                </td>
                <td>
                  {formatNumberString(object.capital_final_review.final_amount)}{' '}
                  {object.capital_final_review.currency}
                </td>
              </tr>
            </tbody>
          </table>
          <table aria-label="Commitment effects on the same trade">
            <thead>
              <tr>
                <th>Balance</th>
                <th>Current Record</th>
                <th>Provisional effect</th>
                <th>Signed effect</th>
                <th>Record after booking</th>
              </tr>
            </thead>
            <tbody>
              {pairedCapitalRows(object.capital_final_review.legs, [
                'current_value',
                'provisional_effect',
                'final_effect',
                'resulting_value',
              ] as const).map((row) => (
                <tr key={row.role}>
                  <th>{row.label}</th>
                  <td>{formatCapitalValue(row.role, row.values.current_value)}</td>
                  <td>
                    {formatCapitalValue(row.role, row.values.provisional_effect)}{' '}
                    {object.capital_final_review!.currency}
                  </td>
                  <td>
                    {formatCapitalValue(row.role, row.values.final_effect)}{' '}
                    {object.capital_final_review!.currency}
                  </td>
                  <td>{formatCapitalValue(row.role, row.values.resulting_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            The signed terms replace the provisional terms on the same trade. Nothing is added to
            the provisional amount, and this does not confirm payment.
          </p>
        </section>
      ) : null}
      {object.initial_capital_review ? (
        <section className="book-record-comparison__capital-calculation">
          <h4>
            Initial commitment · {formatOfficerDate(object.initial_capital_review.financial_date)}
          </h4>
          <table aria-label="Current recorded balances and initial commitment amounts">
            <thead>
              <tr>
                <th>Balance</th>
                <th>Current Record</th>
                <th>This event</th>
              </tr>
            </thead>
            <tbody>
              {pairedCapitalRows(object.initial_capital_review.legs, [
                'current_value',
                'effect_delta',
              ] as const).map((row) => (
                <tr key={row.role}>
                  <th>{row.label}</th>
                  <td>
                    {formatCapitalValue(row.role, row.values.current_value)}
                    {row.currentDate ? ` · ${formatOfficerDate(row.currentDate)}` : ''}
                  </td>
                  <td>
                    {formatCapitalValue(row.role, row.values.effect_delta)}{' '}
                    {object.initial_capital_review!.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            These are the event amounts. A balance includes dated statements and other recorded
            movements.
          </p>
        </section>
      ) : null}
      {deferredIssues.length > 0 ? (
        <section
          className="book-record-comparison__capital-calculation"
          aria-label="Issues retained for review"
          data-testid="capital-deferred-issues"
        >
          <h4>Issues retained for review</h4>
          {deferredIssues.map((note) => (
            <div role="note" key={note.issue_key} data-testid="capital-deferred-issue">
              <p>
                <strong>{note.area === 'cash' ? 'Cash details' : 'Capital terms'}</strong>:{' '}
                {note.issue}{' '}
                <SourceLinks
                  testId="capital-deferred-issue-sources"
                  sources={note.documents.map((document) => ({
                    document,
                    page: document.page,
                    quote: document.snippet,
                  }))}
                />
              </p>
              <p>
                <strong>Why this does not hold this event:</strong> {note.review_reason}{' '}
                <SourceLinks
                  testId="capital-deferred-review-sources"
                  sources={note.review_documents.map((document) => ({
                    document,
                    page: document.page,
                    quote: document.snippet,
                  }))}
                />
              </p>
              {note.missing_references?.map((reference, index) => (
                <p key={index}>
                  Evidence request: {reference.title}{' '}
                  <SourceLinks
                    testId="book-deferred-reference-source"
                    sources={[
                      {
                        document: reference.document,
                        page: reference.document.page,
                        quote: reference.document.snippet,
                      },
                    ]}
                  />
                </p>
              ))}
              <p>
                Review again when:{' '}
                {note.reconsider_on.map((trigger) => reconsiderLabels[trigger]).join(', ')}.
              </p>
            </div>
          ))}
        </section>
      ) : null}
      {object.seller_pre_sale_review ? (
        <p
          role="note"
          className="book-record-comparison__capital-calculation"
          data-testid="seller-pre-sale-note"
        >
          <strong>Seller holding before this sale is not documented</strong>.{' '}
          {object.seller_pre_sale_review.note}
        </p>
      ) : null}
      {noncoreFlags.length > 0 && (
        <section
          className="book-record-comparison__capital-calculation"
          aria-label="Non-core source gaps"
          data-testid="book-noncore-flags"
        >
          <strong>Book with source flags</strong>
          <p>
            Book can proceed. Evidence sign-off waits for missing evidence or a recorded waiver.
          </p>
          {noncoreFlags.map((flag) => (
            <div key={flag.field}>
              <p>
                {flag.period_version_key ? 'Selected period: ' : ''}
                {flag.reason}
              </p>
              <SourceLinks
                testId="book-noncore-flag-sources"
                sources={flag.documents.map((document) => ({
                  document,
                  page: document.page,
                  quote: document.snippet,
                }))}
              />
              {flag.missing_references.map((reference, index) => (
                <p key={index}>
                  {reference.kind === 'information'
                    ? 'Information requested'
                    : 'Document requested'}
                  : {reference.title}{' '}
                  <SourceLinks
                    testId="book-noncore-reference-source"
                    sources={[
                      {
                        document: reference.document,
                        page: reference.document.page,
                        quote: reference.document.snippet,
                      },
                    ]}
                  />
                </p>
              ))}
            </div>
          ))}
        </section>
      )}
      {object.capital_calculation?.notice_content_gaps?.map((gap, index) => (
        <p
          role="note"
          className="book-record-comparison__capital-calculation"
          key={index}
          data-testid="source-call-notice-gap"
        >
          <strong>Notice omits the use of proceeds</strong>. The agreement makes the notice
          effective when sent. This omission does not hold the call. {gap.reason}{' '}
          <SourceLinks
            testId="source-call-notice-gap-sources"
            sources={gap.documents.map((document) => ({
              document,
              page: document.page,
              quote: document.snippet,
            }))}
          />
        </p>
      ))}
      {object.capital_calculation ? (
        <details className="book-record-comparison__capital-calculation">
          <summary>How balances change</summary>
          {object.capital_calculation.missing_requirements?.includes('governing_terms') ? (
            <p role="status">
              Governing agreement not supplied. Review this call against its notice.
            </p>
          ) : null}
          <table aria-label="Commitment balances before and after this trade">
            <thead>
              <tr>
                <th>Balance</th>
                <th>Current Record</th>
                <th>After this trade</th>
              </tr>
            </thead>
            <tbody>
              {(['committed', 'drawn', 'undrawn'] as const).map((key) => (
                <tr key={key}>
                  <th>
                    {key === 'committed' ? 'Commitment' : key === 'drawn' ? 'Drawn' : 'Undrawn'}
                  </th>
                  <td>{formatNumberString(object.capital_calculation!.before[key])}</td>
                  <td>
                    {formatNumberString(object.capital_calculation!.after[key])}{' '}
                    {object.capital_calculation!.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {object.capital_calculation.legs.map((leg) => (
            <div key={leg.position_id}>
              <strong>{leg.position_role === 'commitment' ? 'Commitment' : 'Undrawn'}</strong>
              <p>
                Confirmed balance {formatNumberString(leg.checkpoint_value)} ·{' '}
                {formatOfficerDate(leg.checkpoint_date)}{' '}
                {leg.checkpoint_document_id ? (
                  <DocumentLink document={{ document_id: leg.checkpoint_document_id }} />
                ) : null}
              </p>
              {leg.movements.map((movement) => (
                <p key={movement.trade_id}>
                  {formatOfficerDate(movement.financial_date)} ·{' '}
                  {formatNumberString(movement.units_delta)}{' '}
                  {movement.document_id ? (
                    <DocumentLink document={{ document_id: movement.document_id }} />
                  ) : null}
                </p>
              ))}
              <p>
                This trade: {formatNumberString(leg.effect_delta)} ·{' '}
                {formatOfficerDate(object.capital_calculation!.financial_date)}
              </p>
            </div>
          ))}
        </details>
      ) : null}
      {expanded || object.fields.length > fields.length ? (
        <button
          type="button"
          className="book-record-comparison__expand"
          aria-expanded={expanded}
          data-testid={`book-expand-${object.target_id}`}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Hide other fields' : 'See full object'}
        </button>
      ) : null}
    </section>
  )
}

export default function BookRecordComparison({
  review,
  onAction,
  onFieldChange,
  className,
  pendingActionId,
  embedded = false,
}: BookRecordComparisonProps) {
  const model = useMemo(() => (review ? toBookReviewView(review) : null), [review])
  if (!model) return null
  const globalActions = model.actions.filter(
    (action) => !action.object_id && !action.target_id && !action.proposal_id
  )
  const rootClass = [
    'book-record-comparison',
    embedded ? 'book-record-comparison--embedded' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <section className={rootClass} data-testid="book-record-comparison">
      {!embedded ? (
        <header className="book-record-comparison__band">
          <span>Book</span>
        </header>
      ) : null}
      {!embedded && model.sentence ? (
        <p className="book-record-comparison__sentence">{model.sentence}</p>
      ) : null}
      {!embedded ? (
        <h2 className="book-record-comparison__review-title">Review the Record changes</h2>
      ) : null}
      <div className="book-record-comparison__objects">
        {model.objects.map((object) => (
          <ObjectComparison
            key={`${object.target_id}:${object.proposal_id}`}
            object={object}
            pendingActionId={pendingActionId}
            onAction={onAction}
            onFieldChange={onFieldChange}
          />
        ))}
      </div>
      <ActionButtons
        actions={globalActions}
        pendingActionId={pendingActionId}
        onAction={onAction}
      />
      {!embedded ? <SourceDocumentList documents={[...model.documents]} /> : null}
    </section>
  )
}

function formatCapitalValue(
  role: 'commitment' | 'drawn' | 'undrawn',
  value: string | null
): string {
  const displayed = pairedCapitalDisplayValue(role, value)
  return displayed === null ? 'Unknown' : formatNumberString(displayed)
}
